import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "wouter";
import { useGetRecipe } from "@workspace/api-client-react";
import { Layout } from "@/components/Layout";
import { HeroImageUploadDialog } from "@/components/HeroImageUploadDialog";
import { ScaleControl } from "@/components/validate/ScaleControl";
import { isRealIngredient, scaleAmount } from "@workspace/recipe-utils";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft,
  Clock,
  UtensilsCrossed,
  Flame,
  Users,
  Tag,
  Globe,
  Camera,
  ZapOff,
  Zap,
  ChevronDown,
  ChevronUp,
  X,
  ChevronLeft as CaretLeft,
  ChevronRight as CaretRight,
  BarChart2,
  Pencil,
  Share2,
  ImageDown,
  Loader2,
  Check,
  Printer,
  ShoppingCart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { RecipeNutritionSection } from "@/components/RecipeNutritionSection";
import { FavoriteButton } from "@/components/FavoriteButton";
import { CollectionPicker } from "@/components/CollectionPicker";

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  "main dish":       { bg: "hsl(17 53% 51%)",  text: "hsl(40 33% 97%)" },
  "soup/stew":       { bg: "hsl(25 80% 45%)",  text: "hsl(40 33% 97%)" },
  bread:             { bg: "hsl(45 75% 55%)",  text: "hsl(33 28% 14%)" },
  dessert:           { bg: "hsl(350 55% 75%)", text: "hsl(33 28% 14%)" },
  beverage:          { bg: "hsl(210 50% 60%)", text: "hsl(40 33% 97%)" },
  salad:             { bg: "hsl(145 40% 45%)", text: "hsl(40 33% 97%)" },
  appetizer:         { bg: "hsl(38 85% 55%)",  text: "hsl(33 28% 14%)" },
  side:              { bg: "hsl(90 30% 50%)",  text: "hsl(40 33% 97%)" },
  breakfast:         { bg: "hsl(50 90% 60%)",  text: "hsl(33 28% 14%)" },
  snack:             { bg: "hsl(38 85% 55%)",  text: "hsl(33 28% 14%)" },
  sauce:             { bg: "hsl(91 18% 36%)",  text: "hsl(40 33% 97%)" },
  "sauce/condiment": { bg: "hsl(91 18% 36%)", text: "hsl(40 33% 97%)" },
};
const DEFAULT_COLOR = { bg: "hsl(30 15% 55%)", text: "hsl(40 33% 97%)" };

function getCategoryColor(category?: string | null) {
  if (!category) return DEFAULT_COLOR;
  const key = category.toLowerCase().trim();
  if (CATEGORY_COLORS[key]) return CATEGORY_COLORS[key];
  for (const [k, v] of Object.entries(CATEGORY_COLORS)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return DEFAULT_COLOR;
}

function formatTime(t?: string | null): string | null {
  return t?.replace(/\bminutes?\b/i, "min").replace(/\bhours?\b/i, "hr") ?? null;
}

function SectionTitle({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <h2
      className="text-2xl font-bold text-foreground mb-5 flex items-center gap-2"
      style={{ fontFamily: "var(--font-display)" }}
    >
      {icon}
      {children}
    </h2>
  );
}

interface NutritionSummary {
  calories: number;
  protein_g: number;
  total_carbs_g: number;
  total_fat_g: number;
}

function useNutritionSummary(recipeId: number) {
  const [data, setData] = useState<NutritionSummary | null>(null);
  const [fullData, setFullData] = useState<{
    perServing: NutritionSummary;
    confidence: string | null;
  } | null>(null);

  useEffect(() => {
    setData(null);
    setFullData(null);
    if (recipeId < 0) return;
    let cancelled = false;
    const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
    fetch(`${baseUrl}/api/nutrition/${recipeId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled) return;
        if (json?.nutrition?.perServing) {
          setData(json.nutrition.perServing);
          setFullData({ perServing: json.nutrition.perServing, confidence: json.nutrition.confidence });
        }
      })
      .catch(() => null);
    return () => { cancelled = true; };
  }, [recipeId]);

  return { data, fullData };
}

function WakeLockToggle() {
  const [active, setActive] = useState(false);
  const lockRef = useRef<WakeLockSentinel | null>(null);
  const supported = "wakeLock" in navigator;
  const { toast } = useToast();

  const toggle = useCallback(async () => {
    if (!supported) {
      toast({
        title: "Wake Lock not supported",
        description: "Your browser doesn't support keeping the screen on.",
        variant: "destructive",
      });
      return;
    }
    if (active && lockRef.current) {
      await lockRef.current.release();
      lockRef.current = null;
      setActive(false);
    } else {
      try {
        lockRef.current = await navigator.wakeLock.request("screen");
        setActive(true);
        toast({ title: "Screen will stay on while cooking" });
        lockRef.current.addEventListener("release", () => {
          lockRef.current = null;
          setActive(false);
        });
      } catch {
        toast({
          title: "Could not enable Wake Lock",
          description: "Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [active, supported, toast]);

  useEffect(() => {
    return () => {
      lockRef.current?.release().catch(() => null);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      title={active ? "Screen will stay on while cooking" : "Keep screen on while cooking"}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
        active
          ? "bg-amber-50 border-amber-300 text-amber-700"
          : "bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
      )}
    >
      {active ? <Zap className="w-3.5 h-3.5" /> : <ZapOff className="w-3.5 h-3.5" />}
      {active ? "Screen On" : "Keep Screen On"}
    </button>
  );
}

function NutritionBar({ data }: { data: NutritionSummary }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
      >
        <BarChart2 className="w-3.5 h-3.5" />
        <span>
          ~{Math.round(data.calories)} cal&nbsp;·&nbsp;
          {Math.round(data.protein_g)}g protein&nbsp;·&nbsp;
          {Math.round(data.total_carbs_g)}g carbs
        </span>
        {expanded ? (
          <ChevronUp className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 border border-border rounded-xl p-4 bg-white/80 max-w-xs">
          <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
            Per Serving
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              { label: "Calories", value: Math.round(data.calories), unit: "" },
              { label: "Fat", value: Math.round(data.total_fat_g * 10) / 10, unit: "g" },
              { label: "Carbs", value: Math.round(data.total_carbs_g * 10) / 10, unit: "g" },
              { label: "Protein", value: Math.round(data.protein_g * 10) / 10, unit: "g" },
            ].map(({ label, value, unit }) => (
              <div key={label} className="flex justify-between">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium text-foreground">
                  {value}
                  {unit}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LightboxModal({
  images,
  initialIndex,
  onClose,
}: {
  images: Array<{ id: number; url: string; originalName?: string | null }>;
  initialIndex: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(initialIndex);

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const img = images[idx];
  if (!img) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      <div
        className="relative max-w-3xl max-h-[90vh] mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.url}
          alt={img.originalName ?? "Original recipe"}
          className="max-h-[85vh] max-w-full object-contain rounded-xl shadow-2xl"
        />

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full bg-black/30 hover:bg-black/60 transition-colors"
            >
              <CaretLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full bg-black/30 hover:bg-black/60 transition-colors"
            >
              <CaretRight className="w-5 h-5" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    i === idx ? "bg-white scale-125" : "bg-white/50 hover:bg-white/80"
                  )}
                />
              ))}
            </div>
          </>
        )}

        {img.originalName && (
          <p className="absolute -bottom-8 left-0 right-0 text-center text-white/60 text-xs">
            {img.originalName}
          </p>
        )}
      </div>
    </div>
  );
}

export default function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const recipeId = parseInt(id ?? "", 10);

  const { data: recipe, isLoading, error, refetch } = useGetRecipe(recipeId, {
    query: { enabled: !isNaN(recipeId) },
  });

  const { toast } = useToast();
  const [scale, setScale] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [addingToList, setAddingToList] = useState(false);
  const [addedToList, setAddedToList] = useState(false);

  const { data: nutritionSummary } = useNutritionSummary(isNaN(recipeId) ? -1 : recipeId);

  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/recipe/${recipeId}`;
    const shareTitle = recipe?.title ?? "Recipe";
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: `Check out this recipe: ${shareTitle}`, url });
        return;
      } catch {
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      toast({ title: "Link copied!" });
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      toast({ title: "Copy failed", description: "Please copy the URL from the address bar.", variant: "destructive" });
    }
  }, [recipeId, recipe?.title, toast]);

  const handlePrint = useCallback((includeHero: boolean) => {
    const root = document.documentElement;
    if (includeHero) {
      root.classList.add("print-include-hero");
    } else {
      root.classList.remove("print-include-hero");
    }
    const cleanup = () => {
      root.classList.remove("print-include-hero");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    // Defer one frame so the class change is applied before the print dialog opens
    requestAnimationFrame(() => window.print());
  }, []);

  const handleExport = useCallback(async (format: "png" | "pdf") => {
    setExporting(true);
    try {
      const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
      const res = await fetch(`${baseUrl}/api/recipes/${recipeId}/export-card?format=${format}`);
      if (!res.ok) throw new Error("Failed to generate card");
      const blob = await res.blob();
      const filename = `recipe-${recipeId}.${format}`;

      // On mobile with PNG, try Web Share API first
      if (format === "png" && navigator.canShare?.({ files: [new File([blob], filename, { type: "image/png" })] })) {
        try {
          await navigator.share({
            title: recipe?.title ?? "Recipe",
            files: [new File([blob], filename, { type: "image/png" })],
          });
          return;
        } catch {
        }
      }

      // Fallback: download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: `Recipe card downloaded as ${format.toUpperCase()}` });
    } catch {
      toast({ title: "Export failed", description: "Could not generate the recipe card.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  }, [recipeId, recipe?.title, toast]);

  const handleAddToShoppingList = useCallback(async () => {
    setAddingToList(true);
    try {
      const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
      const res = await fetch(
        `${baseUrl}/api/shopping-list/recipe/${recipeId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ multiplier: scale }),
        },
      );
      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(json.error ?? `Failed (${res.status})`);
      }
      const json = (await res.json()) as { added: number };
      setAddedToList(true);
      toast({
        title: "Added to shopping list",
        description: `${json.added} ingredient${json.added === 1 ? "" : "s"}${
          scale !== 1 ? ` (scaled ×${scale})` : ""
        } added.`,
      });
      setTimeout(() => setAddedToList(false), 2500);
    } catch (err) {
      toast({
        title: "Couldn't add to shopping list",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setAddingToList(false);
    }
  }, [recipeId, scale, toast]);

  if (isNaN(recipeId)) {
    return (
      <Layout>
        <div className="py-20 text-center text-muted-foreground">Invalid recipe ID.</div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 space-y-6 py-8">
          <div className="h-8 w-32 rounded-xl bg-muted animate-pulse" />
          <div className="aspect-[16/7] rounded-3xl bg-muted animate-pulse" />
          <div className="h-12 w-2/3 rounded-xl bg-muted animate-pulse" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !recipe) {
    return (
      <Layout>
        <div className="py-20 text-center">
          <p className="text-lg font-medium">Recipe not found</p>
          <Link href="/" className="text-primary hover:underline text-sm mt-2 inline-block">
            ← Back to cookbook
          </Link>
        </div>
      </Layout>
    );
  }

  const { bg: categoryBg, text: categoryText } = getCategoryColor(recipe.category);
  const heroUrl = recipe.heroImagePath
    ? `/api/recipes/hero-images/file/${recipe.id}-hero.jpg`
    : null;
  const sourceImages = (recipe as { sourceImages?: Array<{ id: number; url: string; originalName?: string | null; isPrimary?: boolean }> }).sourceImages ?? [];

  const ingredients = recipe.ingredients ?? [];
  const hasDirections = recipe.directions?.trim();
  const hasNotes = recipe.handwrittenNotes?.trim();

  const prepTime = formatTime(recipe.prepTime);
  const cookTime = formatTime(recipe.cookTime);
  const totalTime = formatTime(recipe.totalTime);

  const directions = hasDirections
    ? recipe.directions
        .split(/\n/)
        .map((line) => line.trim())
        .filter(Boolean)
    : [];

  const stickyRotation = ((recipe.id % 3) === 0 ? -1 : recipe.id % 2 === 0 ? -1.5 : -2);

  const titleLetter = (
    recipe.title.match(/[A-Za-z0-9]/)?.[0] ?? recipe.title.charAt(0)
  ).toUpperCase();

  return (
    <Layout>
      <div className="max-w-[800px] mx-auto">

        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div data-print-hide className="flex items-center justify-between px-4 sm:px-6 py-4">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Cookbook
          </Link>
          <div className="flex items-center gap-2">
            <FavoriteButton
              recipeId={recipe.id}
              isFavorite={(recipe as { isFavorite?: boolean | null }).isFavorite === true}
              variant="detail"
            />
            <CollectionPicker recipeId={recipe.id} variant="pill" />

            {/* Share button */}
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 h-8 text-sm"
              onClick={handleShare}
              title="Share recipe"
            >
              {shareCopied ? (
                <Check className="w-3.5 h-3.5 text-green-600" />
              ) : (
                <Share2 className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">
                {shareCopied ? "Copied!" : "Share"}
              </span>
            </Button>

            {/* Export Card dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 h-8 text-sm"
                  disabled={exporting}
                  title="Export recipe card"
                >
                  {exporting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ImageDown className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">
                    {exporting ? "Generating…" : "Export Card"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("png")}>
                  Image (PNG)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>
                  PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Print dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 h-8 text-sm"
                  title="Print recipe"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Print</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handlePrint(false)}>
                  Print recipe
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handlePrint(true)}>
                  Print with photo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link href={`/recipe/${id}/edit`}>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 h-8 text-sm"
              >
                <Pencil className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            </Link>
            <WakeLockToggle />
          </div>
        </div>

        {/* ── Hero image / placeholder banner ─────────────────────────────── */}
        <div className="relative" data-print-hero>
          {heroUrl ? (
            <div className="relative aspect-[16/7] overflow-hidden group">
              <img
                src={heroUrl}
                alt={recipe.title}
                className="w-full h-full object-cover"
              />
              <div data-print-hide className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <HeroImageUploadDialog
                  recipeId={recipe.id}
                  hasHero={true}
                  heroUrl={heroUrl}
                  onSuccess={() => refetch()}
                  trigger={
                    <button className="bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-xl hover:bg-black/70 transition-colors flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5" />
                      Change Photo
                    </button>
                  }
                />
              </div>
            </div>
          ) : (
            <div
              className="aspect-[16/7] flex items-center justify-center relative overflow-hidden group"
              style={{ backgroundColor: categoryBg }}
            >
              <span
                className="text-[10rem] font-bold select-none"
                style={{
                  fontFamily: "var(--font-display)",
                  color: categoryText,
                  opacity: 0.25,
                }}
              >
                {titleLetter}
              </span>
              <div data-print-hide className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <HeroImageUploadDialog
                  recipeId={recipe.id}
                  hasHero={false}
                  heroUrl={null}
                  onSuccess={() => refetch()}
                  trigger={
                    <button className="bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-xl hover:bg-black/60 transition-colors flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5" />
                      Add Photo
                    </button>
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Header section ───────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 pt-8 pb-6 space-y-4">
          {recipe.category && (
            <div
              data-print-pill
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
              style={{ backgroundColor: `${categoryBg}20`, color: categoryBg, border: `1px solid ${categoryBg}40` }}
            >
              <Tag className="w-3.5 h-3.5" />
              {recipe.category}
            </div>
          )}

          <h1
            className="text-3xl sm:text-4xl font-bold text-foreground leading-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {recipe.title}
          </h1>

          {(recipe.authorSource || recipe.cuisine) && (
            <p
              className="text-muted-foreground"
              style={{ fontFamily: "var(--font-handwritten)", fontSize: "1.2rem" }}
            >
              {[recipe.authorSource, recipe.cuisine].filter(Boolean).join(" · ")}
            </p>
          )}

          {/* Metadata bar */}
          {(prepTime || cookTime || totalTime || recipe.yieldServings || recipe.difficulty) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {prepTime && (
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-foreground">Prep</span>
                  <span className="text-muted-foreground">{prepTime}</span>
                </div>
              )}
              {cookTime && (
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-sm">
                  <UtensilsCrossed className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-foreground">Cook</span>
                  <span className="text-muted-foreground">{cookTime}</span>
                </div>
              )}
              {totalTime && (
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-foreground">Total</span>
                  <span className="text-muted-foreground">{totalTime}</span>
                </div>
              )}
              {recipe.yieldServings && (
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-sm">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-foreground">Serves</span>
                  <span className="text-muted-foreground">{recipe.yieldServings}</span>
                </div>
              )}
              {recipe.caloriesPerServing != null && !nutritionSummary && (
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-sm">
                  <Flame className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-foreground">{recipe.caloriesPerServing}</span>
                  <span className="text-muted-foreground">cal/serving</span>
                </div>
              )}
              {recipe.difficulty && (
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-sm">
                  <span className="font-medium text-foreground">Difficulty:</span>
                  <span className="text-muted-foreground">{recipe.difficulty}</span>
                </div>
              )}
            </div>
          )}

          {/* Nutrition summary — click to expand */}
          {nutritionSummary && (
            <div data-print-hide>
              <NutritionBar data={nutritionSummary} />
            </div>
          )}
        </div>

        {/* ── Divider ────────────────────────────────────────────────────────── */}
        <div className="mx-4 sm:mx-6 border-t border-border/60" />

        {/* ── Main content ────────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 pb-16 space-y-10 pt-8">

          {/* Section 2: Ingredients */}
          {ingredients.length > 0 && (
            <section>
              <SectionTitle>Ingredients</SectionTitle>
              <div data-print-hide className="mb-4 flex flex-wrap items-center gap-3">
                <ScaleControl scale={scale} onChange={setScale} />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddToShoppingList}
                  disabled={addingToList}
                  className="gap-1.5"
                  data-testid="button-add-to-shopping-list"
                  data-print-hide
                >
                  {addingToList ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : addedToList ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <ShoppingCart className="w-4 h-4" />
                  )}
                  {addedToList ? "Added" : "Add to shopping list"}
                </Button>
              </div>
              <ul className="space-y-1.5">
                {ingredients.map((ing, i) => {
                  const rowKey = ing.id ?? i;
                  const hasIngredient = isRealIngredient(ing);
                  const scaledAmount = scaleAmount(ing.amount, scale);
                  const isScaled = scale !== 1 && ing.amount;
                  return (
                    <React.Fragment key={rowKey}>
                      {ing.sectionHeader?.trim() && (
                        <li className="pt-4 pb-1 first:pt-0">
                          <h4
                            className="text-sm font-semibold text-muted-foreground uppercase tracking-widest"
                            style={{ fontFamily: "var(--font-display)" }}
                          >
                            {ing.sectionHeader}
                          </h4>
                        </li>
                      )}
                      {hasIngredient && (
                        <li className="flex items-start gap-3 py-1.5 border-b border-border/30 last:border-0">
                          <span
                            className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: categoryBg }}
                          />
                          <span className="text-foreground text-sm sm:text-base flex-1">
                            {isScaled ? (
                              <span
                                className="font-semibold"
                                style={{ color: categoryBg }}
                                title={`Original: ${ing.amount}`}
                              >
                                {scaledAmount}
                              </span>
                            ) : (
                              ing.amount && (
                                <span className="font-semibold text-foreground">
                                  {ing.amount}
                                </span>
                              )
                            )}
                            {ing.unit && (
                              <span className="font-medium text-foreground ml-1">
                                {ing.unit}
                              </span>
                            )}
                            <span className="ml-1">{ing.item}</span>
                            {ing.prepNotes && (
                              <span className="text-muted-foreground text-sm ml-1.5">
                                ({ing.prepNotes})
                              </span>
                            )}
                          </span>
                        </li>
                      )}
                    </React.Fragment>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Section 3: Directions */}
          {hasDirections && (
            <section>
              <SectionTitle>Directions</SectionTitle>
              <ol className="space-y-5">
                {directions.map((step, i) => {
                  const isNumberedStep = /^\d+[\.\)]\s/.test(step);
                  const displayText = isNumberedStep
                    ? step.replace(/^\d+[\.\)]\s+/, "")
                    : step;
                  return (
                    <li key={i} className="flex gap-4">
                      <span
                        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold mt-0.5"
                        style={{ backgroundColor: `${categoryBg}20`, color: categoryBg, border: `1.5px solid ${categoryBg}40` }}
                      >
                        {i + 1}
                      </span>
                      <p className="text-foreground leading-relaxed text-sm sm:text-base flex-1 pt-1">
                        {displayText}
                      </p>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {/* Section 4 + 5: Notes & Serving Suggestions (side by side on desktop) */}
          {(hasNotes || recipe.servingSuggestions?.trim()) && (
            <div className={cn(
              "gap-6",
              hasNotes && recipe.servingSuggestions?.trim()
                ? "flex flex-col lg:flex-row lg:items-start"
                : ""
            )}>

              {/* Handwritten Notes sticky note */}
              {hasNotes && (
                <div
                  className="lg:order-2 lg:flex-shrink-0"
                  style={{ maxWidth: recipe.handwrittenNotes!.length > 120 ? "min(420px, 100%)" : "min(300px, 100%)" }}
                >
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    ✍️ From the original binder
                  </p>
                  <div
                    data-print-clean
                    className="relative px-5 pb-5 pt-8 bg-amber-50 rounded shadow-md border border-amber-200/60"
                    style={{
                      transform: `rotate(${stickyRotation}deg)`,
                      boxShadow: "2px 4px 12px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)",
                    }}
                  >
                    {/* Tape strip */}
                    <div
                      data-print-hide
                      className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-16 h-5 rounded-sm"
                      style={{
                        background: "rgba(255,220,100,0.55)",
                        border: "1px solid rgba(200,170,50,0.25)",
                        backdropFilter: "blur(1px)",
                      }}
                    />
                    <p
                      className="text-foreground leading-relaxed whitespace-pre-wrap"
                      style={{
                        fontFamily: "var(--font-handwritten)",
                        fontSize: "1.15rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {recipe.handwrittenNotes}
                    </p>
                  </div>
                </div>
              )}

              {/* Serving Suggestions */}
              {recipe.servingSuggestions?.trim() && (
                <section className="lg:order-1 lg:flex-1">
                  <SectionTitle>Serving Suggestions</SectionTitle>
                  <p className="text-foreground leading-relaxed italic text-sm sm:text-base">
                    {recipe.servingSuggestions}
                  </p>
                </section>
              )}
            </div>
          )}

          {/* Section 5.5: Nutrition Facts (collapsible, display page) */}
          {nutritionSummary && (
            <div data-print-hide>
              <RecipeNutritionSection
                recipeId={recipeId}
                nutritionSummary={nutritionSummary}
                scale={scale}
              />
            </div>
          )}

          {/* Section 6: Footer */}
          <section className="space-y-5 pt-2 border-t border-border/60">
            <div className="flex flex-wrap gap-2 pt-4">
              {recipe.category && (
                <span
                  data-print-pill
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border"
                  style={{
                    backgroundColor: `${categoryBg}15`,
                    color: categoryBg,
                    borderColor: `${categoryBg}40`,
                  }}
                >
                  <Tag className="w-3 h-3" />
                  {recipe.category}
                </span>
              )}
              {recipe.cuisine && (
                <span data-print-pill className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border bg-secondary/10 text-secondary border-secondary/30">
                  <Globe className="w-3 h-3" />
                  {recipe.cuisine}
                </span>
              )}
            </div>

            <div data-print-hide className="flex flex-wrap gap-3">
              {sourceImages.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted border border-border text-sm font-medium text-foreground hover:bg-muted/80 hover:border-primary/40 transition-all"
                >
                  <Camera className="w-4 h-4" />
                  View Original Recipe Page
                  {sourceImages.length > 1 && (
                    <span className="text-xs text-muted-foreground">({sourceImages.length})</span>
                  )}
                </button>
              )}

              {heroUrl && (
                <div className="relative w-12 h-9 rounded-lg overflow-hidden border border-border/60 flex-shrink-0">
                  <img src={heroUrl} alt="Hero" className="w-full h-full object-cover" />
                </div>
              )}
              <HeroImageUploadDialog
                recipeId={recipe.id}
                hasHero={!!recipe.heroImagePath}
                heroUrl={heroUrl}
                onSuccess={() => refetch()}
              />
            </div>
          </section>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && sourceImages.length > 0 && (
        <LightboxModal
          images={sourceImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </Layout>
  );
}
